/**
 * PHOTON-80 — inline SVG sparkline (design-spec §6.4). No chart library.
 *
 * Three treatments: healthy (solid stroke, area fill, filled endpoint),
 * stale (dashed stroke, half-opacity area, hollow endpoint), failed (flat
 * ghost line, no area, no endpoint). Each gradient gets a unique DOM id via
 * useId — duplicate ids cause cross-card fill bleed.
 */
import React, { useId } from "react";

export type SparkMode = "healthy" | "stale" | "failed";

interface SparklineProps {
  /** y-coordinates on the 0-46 grid; ignored in failed mode. */
  points?: number[];
  /** CSS color expression for stroke/fill, e.g. "var(--sev-warn)". */
  color: string;
  mode: SparkMode;
  /** Area gradient top opacity (mockup uses .28-.30 healthy, .14 stale). */
  areaOpacity?: number;
}

export const Sparkline: React.FC<SparklineProps> = ({
  points,
  color,
  mode,
  areaOpacity,
}) => {
  const gradientId = useId().replace(/:/g, "_");

  if (mode === "failed" || !points || points.length === 0) {
    return (
      <svg height={46} viewBox="0 0 300 46" preserveAspectRatio="none" aria-hidden="true">
        <path
          d="M0,23 L300,23"
          fill="none"
          stroke="var(--stroke-default)"
          strokeWidth={2}
          strokeDasharray="3 4"
        />
      </svg>
    );
  }

  // Spread points evenly across the 300-unit width regardless of count, so
  // a short live history (2-3 refreshes) still fills the card edge to edge.
  const xs = points.map((_, i) =>
    points.length === 1 ? 300 : (300 * i) / (points.length - 1),
  );
  const line = points
    .map((y, i) => `${i === 0 ? "M" : "L"}${xs[i].toFixed(1)},${y.toFixed(1)}`)
    .join(" ");
  const area = `${line} L300,46 L0,46 Z`;
  const endX = xs[points.length - 1];
  const endY = points[points.length - 1];
  const topOpacity = areaOpacity ?? (mode === "stale" ? 0.14 : 0.28);

  return (
    <svg height={46} viewBox="0 0 300 46" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={topOpacity} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradientId})`} />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinejoin="round"
        strokeDasharray={mode === "stale" ? "4 3" : undefined}
        opacity={mode === "stale" ? 0.55 : 1}
      />
      {mode === "stale" ? (
        <circle cx={endX} cy={endY} r={3.4} fill="none" stroke={color} strokeWidth={2} opacity={0.7} />
      ) : (
        <circle cx={endX} cy={endY} r={3.4} fill={color} />
      )}
    </svg>
  );
};

export default Sparkline;
